<template>
  <div class="v2-card v2-dashboard-wishlist">
    <div class="v2-card-header">
      <h3 class="v2-heading-sm">
        Wishlists
      </h3>
      <button
        class="v2-header-cta"
        type="button"
        @click="goToWishlists"
      >
        Go to Wishlists
      </button>
    </div>

    <div
      v-if="isLoaded"
      class="v2-card-body"
    >
      <section
        v-if="wishlists.length"
        class="v2-wishlist-grid"
      >
        <v2-wishlist-card
          v-for="wishlist in wishlists"
          :key="wishlist._id"
          :wishlist="wishlist"
          :is-dashboard-page="true"
        />
      </section>

      <div
        v-else
        class="v2-empty-state"
      >
        <div class="v2-empty-icon">
          <feather-icon
            icon="SearchIcon"
            size="22"
          />
        </div>
        <p class="v2-empty-text v2-text-sm">
          Discover your favourite brands and<br>
          create beautiful custom linesheets.
        </p>
        <router-link
          v-if="isEnabledMarketplace"
          :to="{ name: 'explore' }"
          class="v2-empty-cta"
        >
          Explore
        </router-link>
        <span
          v-else
          class="v2-empty-cta v2-empty-cta-disabled"
          :title="marketplaceAccessDeniedMessage"
          aria-disabled="true"
          role="button"
        >
          Explore
        </span>
      </div>
    </div>

    <div
      v-else
      class="v2-loading"
    >
      <div
        class="v2-spinner"
        aria-label="Loading"
      />
    </div>
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import constants, { marketplaceAccessDeniedMessage } from '@/constants'
import { useDashboardWishlist } from '@/composables/useDashboardWishlist'
import V2WishlistCard from '@/views/v2/wishlist/V2WishlistCard.vue'

export default {
  name: 'V2DashboardWishlist',
  components: { V2WishlistCard },
  data() {
    return {
      marketplaceAccessDeniedMessage,
    }
  },
  setup() {
    // V2 dashboard shows up to 6 wishlists in a single horizontal scroll row.
    // Legacy /dashboard keeps the 4-item default.
    const page = useDashboardWishlist({ limit: 6 })
    return { ...page }
  },
  mounted() {
    this.fetchWishlist()
  },
  methods: {
    goToWishlists() {
      this.$router.push({ name: 'v2-wishlist' })
      analytics.track(
        constants.TRACKS.ACTIONS.USER_CLICKS_GO_TO_WISHLISTS_BTN_ON_DASHBOARD,
        this.profile,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-dashboard-wishlist {
  display: flex;
  flex-direction: column;
}

.v2-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.25rem 0;
}

.v2-header-cta {
  background: transparent;
  border: 1px solid var(--v2-primary);
  color: var(--v2-primary);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.v2-header-cta:hover,
.v2-header-cta:focus-visible {
  background: var(--v2-primary);
  color: var(--v2-primary-foreground);
  outline: none;
}

.v2-card-body {
  padding: 1rem 1.25rem 1.25rem;
}

.v2-wishlist-grid {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  /* vertical padding gives V2WishlistCard's hover transform/shadow room
     inside the scroll clipping rectangle (overflow-x:auto implicitly
     clips overflow-y). */
  padding: 0.5rem 0 0.75rem;
  /* thin scrollbar; fall back to default on browsers that ignore these */
  scrollbar-width: thin;
}

.v2-wishlist-grid > * {
  flex: 0 0 280px;
}

.v2-wishlist-grid::-webkit-scrollbar { height: 6px; }
.v2-wishlist-grid::-webkit-scrollbar-thumb {
  background: var(--v2-border);
  border-radius: 3px;
}
.v2-wishlist-grid::-webkit-scrollbar-thumb:hover {
  background: var(--v2-muted-foreground);
}

.v2-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.v2-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--v2-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--v2-muted-foreground);
}

.v2-empty-text {
  margin: 0;
  color: var(--v2-muted-foreground);
}

.v2-empty-cta {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: var(--v2-primary);
  color: var(--v2-primary-foreground);
  border-radius: 8px;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  transition: opacity 0.15s;
}

.v2-empty-cta:hover,
.v2-empty-cta:focus-visible {
  opacity: 0.9;
  color: var(--v2-primary-foreground);
  text-decoration: none;
  outline: none;
}

.v2-empty-cta-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}

.v2-empty-cta-disabled:hover,
.v2-empty-cta-disabled:focus-visible {
  opacity: 0.5;
}

.v2-loading {
  padding: 2rem;
  display: flex;
  justify-content: center;
}

.v2-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--v2-border);
  border-top-color: var(--v2-primary);
  border-radius: 50%;
  animation: v2-spin 0.8s linear infinite;
}

@keyframes v2-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .v2-card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
